import { useEffect, useState, useRef, useCallback } from 'react';

declare const __BUILD_VERSION__: string;

const POLL_INTERVAL = 60_000; // 60 seconds

interface AppVersionState {
  updateAvailable: boolean;
  refresh: () => void;
}

export function useAppVersion(): AppVersionState {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.version && data.version !== __BUILD_VERSION__) {
          if (!cancelled) {
            setUpdateAvailable(true);
            // Stop polling once mismatch is found
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }
      } catch {
        // Network error — ignore, will retry next interval
      }
    }

    // Initial check after a short delay (don't block app load)
    const timeout = setTimeout(check, 5_000);
    // Then poll every 60s
    intervalRef.current = setInterval(check, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, refresh };
}
